import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { FileText, Image, Download, FileUp, Folder, ChevronRight, ChevronDown, Trash2 } from 'lucide-react';
import { FileDoc } from '../../../shared/types/file.types';
import useFile, { FileFilters, FileCreateData } from '../wrappers/use-file';
import { useTranslation } from 'react-i18next';
import { formatFileSize } from '../utils/formatFileSize';
import { useAuth } from '../contexts/AuthContext';
import {
    documentListSnackbarAnchorOrigin,
    documentListStyles,
    fileIconStyle,
    folderChevronStyle,
    getDocumentDeleteActionStyles,
    getDocumentRowStyles,
    imageFileIconColor,
    pdfFileIconColor,
    defaultFileIconColor,
} from './DocumentList.styles';

export interface DocumentGroup {
    label: string;
    predicate: (f: FileDoc) => boolean;
}

interface DocumentListProps {
    /** Filtry pro načtení dokumentů z Firestore */
    filters: FileFilters;
    /** Data pro nahrání nového souboru. Pokud není zadáno, tlačítko pro nahrání se nezobrazí. */
    uploadData?: FileCreateData;
    /** Pokud je zadáno, dokumenty se zobrazí ve složkové struktuře dle skupin. */
    groups?: DocumentGroup[];
    /**
     * Pokud je zadáno, komponenta vykreslí vlastní header sekce s nadpisem a tlačítkem pro nahrání
     * vedle sebe (vhodné pro použití přímo uvnitř Paper bez separátního header Boxu).
     */
    sectionTitle?: string;
}

const DocumentList: React.FC<DocumentListProps> = ({ filters, uploadData, groups, sectionTitle }) => {
    const { t } = useTranslation();
    const { create: createFile, list: listFiles, remove: removeFile } = useFile();
    const { user } = useAuth();
    const [files, setFiles] = useState<FileDoc[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [openGroups, setOpenGroups] = useState<Set<number>>(new Set());
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const filtersKey = JSON.stringify(filters);

    useEffect(() => {
        let cancelled = false;
        const fetch = async () => {
            setLoading(true);
            try {
                const result = await listFiles(filters);
                if (!cancelled) setFiles(result);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetch();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtersKey]);

    const fetchFiles = async () => {
        setLoading(true);
        try {
            setFiles(await listFiles(filters));
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = '';
        setUploading(true);
        try {
            await createFile(file, uploadData);
            await fetchFiles();
        } catch (err) {
            console.error(err);
            setErrorMsg(t('documentList.uploadError'));
        } finally {
            setUploading(false);
        }
    };

    const canDelete = (f: FileDoc) =>
        user?.role === 'admin' || user?.uid === f.createdBy;

    const handleDelete = async (f: FileDoc, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!confirm(t('documentList.deleteConfirm', { name: f.name }))) return;
        setDeletingId(f.id);
        try {
            await removeFile(f);
            setFiles(prev => prev.filter(x => x.id !== f.id));
        } catch (err) {
            console.error(err);
            setErrorMsg(t('documentList.deleteError'));
        } finally {
            setDeletingId(null);
        }
    };

    const getFileIcon = (mimeType: string) => {
        if (mimeType.startsWith('image/')) return <Image size={20} color={imageFileIconColor} style={fileIconStyle} />;
        if (mimeType === 'application/pdf') return <FileText size={20} color={pdfFileIconColor} style={fileIconStyle} />;
        return <FileText size={20} color={defaultFileIconColor} style={fileIconStyle} />;
    };

    const renderFileRow = (f: FileDoc) => (
        <Box
            key={f.id}
            component="a"
            href={f.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={getDocumentRowStyles()}
        >
            <Box sx={documentListStyles.rowContent}>
                {getFileIcon(f.mimeType)}
                <Box sx={documentListStyles.rowNameContainer}>
                    <Typography variant="body2" noWrap>{f.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {f.mimeType.split('/')[1]?.toUpperCase() ?? f.mimeType}, {formatFileSize(f.size)}
                    </Typography>
                </Box>
            </Box>
            <Box sx={documentListStyles.rowMeta}>
                <Typography variant="caption" color="text.secondary" sx={documentListStyles.rowMetaDate}>
                    {f.createdAt.toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    {' '}
                    {f.createdAt.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}
                </Typography>
                <Download size={18} color="#bdbdbd" />
                {canDelete(f) && (
                    deletingId === f.id
                        ? <CircularProgress size={16} sx={documentListStyles.deletingProgress} />
                        : <Box
                            component="span"
                            onClick={(e) => handleDelete(f, e as React.MouseEvent)}
                            sx={getDocumentDeleteActionStyles()}
                        >
                            <Trash2 size={16} />
                        </Box>
                )}
            </Box>
        </Box>
    );

    const toggleGroup = (idx: number) => {
        setOpenGroups(prev => {
            const next = new Set(prev);
            if (next.has(idx)) next.delete(idx);
            else next.add(idx);
            return next;
        });
    };

    const renderGroup = (label: string, groupFiles: FileDoc[], idx: number) => {
        const isRoot = idx === 0;
        const isOpen = isRoot || openGroups.has(idx);

        if (isRoot) {
            return (
                <Box key={label} sx={documentListStyles.rootGroupContainer}>
                    {groupFiles.map(renderFileRow)}
                </Box>
            );
        }

        return (
            <Box key={label} sx={documentListStyles.groupContainer}>
                <Box
                    onClick={() => toggleGroup(idx)}
                    sx={documentListStyles.groupHeader}
                >
                    <Folder size={18} color="#f59e0b" />
                    <Typography variant="body2" fontWeight={600}>{label}</Typography>
                    <Typography variant="caption" color="text.secondary">({groupFiles.length})</Typography>
                    {isOpen
                        ? <ChevronDown size={14} color="#9e9e9e" style={folderChevronStyle} />
                        : <ChevronRight size={14} color="#9e9e9e" style={folderChevronStyle} />
                    }
                </Box>
                {isOpen && groupFiles.map(renderFileRow)}
            </Box>
        );
    };

    const uploadButton = uploadData ? (
        <Button
            component="label"
            variant="outlined"
            size="small"
            startIcon={uploading ? <CircularProgress size={14} color="inherit" /> : <FileUp size={16} />}
            disabled={uploading}
        >
            {t('documentList.uploadButton')}
            <input type="file" hidden onChange={handleUpload} />
        </Button>
    ) : null;

    const content = loading ? (
        <Box sx={documentListStyles.loadingContainer}>
            <CircularProgress size={24} />
        </Box>
    ) : files.length === 0 ? (
        <Typography variant="body2" color="text.secondary">{t('documentList.empty')}</Typography>
    ) : groups ? (
        <Box sx={documentListStyles.groupedContent}>
            {groups.map((g, idx) => {
                const groupFiles = files.filter(g.predicate).sort((a, b) => a.name.localeCompare(b.name, 'cs'));
                return groupFiles.length > 0 ? renderGroup(g.label, groupFiles, idx) : null;
            })}
        </Box>
    ) : (
        <Box sx={documentListStyles.simpleListContent}>
            {[...files].sort((a, b) => a.name.localeCompare(b.name, 'cs')).map(renderFileRow)}
        </Box>
    );

    if (sectionTitle) {
        return (
            <Box>
                <Box sx={documentListStyles.sectionHeader}>
                    <Typography variant="subtitle1" fontWeight={600}>{sectionTitle}</Typography>
                    {uploadButton}
                </Box>
                <Box sx={documentListStyles.sectionContent}>{content}</Box>
            </Box>
        );
    }

    return (
        <>
            <Box sx={documentListStyles.rootContainer}>
                {uploadButton && (
                    <Box sx={documentListStyles.uploadButtonRow}>{uploadButton}</Box>
                )}
                {content}
            </Box>
            <Snackbar open={!!errorMsg} autoHideDuration={5000} onClose={() => setErrorMsg(null)} anchorOrigin={documentListSnackbarAnchorOrigin}>
                <MuiAlert severity="error" onClose={() => setErrorMsg(null)}>{errorMsg}</MuiAlert>
            </Snackbar>
        </>
    );
};

export default DocumentList;
